import React, { useCallback, useMemo, useState } from 'react'
import { DatePicker, Form, Space } from 'antd'
import {
  capitalize,
  filter,
  find,
  flatten,
  get,
  includes,
  intersection,
  isEmpty,
  isNil,
  keys,
  map,
  omit,
  reduce,
  some,
  uniqBy,
  values,
} from 'lodash'
import moment from 'moment'
import PropTypes from 'prop-types'

import {
  DROPDOWN_MIN_WIDTH,
  EVENT_FILTER_ACTION_GROUPS,
  EVENT_METADATA_TYPES,
  EVENT_SUBTYPES,
  EVENT_SUBTYPES_GROUPED_BY_EVENT_METADATA_TYPE,
  EVENT_TYPES,
  PERMISSIONS,
} from '../../constants'
import { useFormDirty } from '../../hooks'
import { idPropType, optionPropType } from '../../prop-types'
import {
  filterSelectOptions,
  getFormItemLabelAboveConfiguration,
} from '../../utils'

import FormButtons from './form-buttons'
import FormInput from './form-input'
import FormSelect from './form-select'

const HistoryDrawerFilter = ({
  userOptions,
  createdByIds,
  startAt,
  endAt,
  types,
  subtypes,
  searchTerm,
  exactSearchTerm,
  filterEventTypes,
  eventMetadataTypes,
  onFilter,
}) => {
  const [form] = Form.useForm()
  const { isDirty, setToDirty, resetForm } = useFormDirty(form)

  const initialValues = useMemo(
    () => ({
      createdByIds: map(createdByIds, String),
      dateRange:
        isNil(startAt) && isNil(endAt) ? [] : [moment(startAt), moment(endAt)],
      types,
      subtypes,
      searchTerm,
      exactSearchTerm,
    }),
    [
      createdByIds,
      startAt,
      endAt,
      types,
      subtypes,
      searchTerm,
      exactSearchTerm,
    ],
  )

  const typeOptions = useMemo(
    () =>
      map(keys(EVENT_TYPES), eventType => ({
        value: eventType,
        label: capitalize(get(EVENT_TYPES, eventType)),
      })),
    [],
  )

  const {
    shouldShowActionGroups,
    actionGroupOptions,
    subtypeByActionGroupOptions,
  } = useMemo(() => {
    const addedActions = !isEmpty(
      intersection(keys(EVENT_FILTER_ACTION_GROUPS.ADDED), eventMetadataTypes),
    )
      ? flatten(
          map(eventMetadataTypes, metadataType =>
            values(EVENT_FILTER_ACTION_GROUPS.ADDED[metadataType]),
          ),
        )
      : []

    const removedActions = !isEmpty(
      intersection(
        keys(EVENT_FILTER_ACTION_GROUPS.REMOVED),
        eventMetadataTypes,
      ),
    )
      ? flatten(
          map(eventMetadataTypes, metadataType =>
            values(EVENT_FILTER_ACTION_GROUPS.REMOVED[metadataType]),
          ),
        )
      : []

    const updatedActions = !isEmpty(
      intersection(
        keys(EVENT_FILTER_ACTION_GROUPS.UPDATED),
        eventMetadataTypes,
      ),
    )
      ? flatten(
          map(eventMetadataTypes, metadataType =>
            values(EVENT_FILTER_ACTION_GROUPS.UPDATED[metadataType]),
          ),
        )
      : []

    const movedActions = !isEmpty(
      intersection(keys(EVENT_FILTER_ACTION_GROUPS.MOVED), eventMetadataTypes),
    )
      ? flatten(
          map(eventMetadataTypes, metadataType =>
            values(EVENT_FILTER_ACTION_GROUPS.MOVED[metadataType]),
          ),
        )
      : []

    const receivedActions = !isEmpty(
      intersection(
        keys(EVENT_FILTER_ACTION_GROUPS.RECEIVED),
        eventMetadataTypes,
      ),
    )
      ? flatten(
          map(eventMetadataTypes, metadataType =>
            values(EVENT_FILTER_ACTION_GROUPS.RECEIVED[metadataType]),
          ),
        )
      : []

    const actionGroups = {
      ADDED: addedActions,
      REMOVED: removedActions,
      UPDATED: updatedActions,
      MOVED: movedActions,
      RECEIVED: receivedActions,
    }

    const nonEmptyActionGroups = reduce(
      keys(actionGroups),
      (acc, key) => {
        if (!isEmpty(actionGroups[key])) {
          acc[key] = actionGroups[key]
        }

        return acc
      },
      {},
    )

    const actionGroupOptions = map(keys(nonEmptyActionGroups), actionGroup => ({
      value: actionGroup,
      label: capitalize(actionGroup),
    }))

    const subtypeByActionGroupOptions = reduce(
      keys(nonEmptyActionGroups),
      (acc, actionGroup) => {
        acc[actionGroup] = map(nonEmptyActionGroups[actionGroup], action => ({
          value: action.value,
          label: action.label,
        }))

        return acc
      },
      {},
    )

    return {
      shouldShowActionGroups: !isEmpty(nonEmptyActionGroups),
      actionGroupOptions,
      subtypeByActionGroupOptions,
    }
  }, [eventMetadataTypes])

  const subtypeOptions = useMemo(
    () =>
      uniqBy(
        flatten(
          map(
            filter(keys(EVENT_SUBTYPES_GROUPED_BY_EVENT_METADATA_TYPE), type =>
              includes(eventMetadataTypes, type),
            ),
            type => get(EVENT_SUBTYPES_GROUPED_BY_EVENT_METADATA_TYPE, type),
          ),
        ),
        'value',
      ),
    [eventMetadataTypes],
  )

  const datePickerRangeOptions = useMemo(
    () => ({
      Today: [moment().startOf('day'), moment().endOf('day')],
      'This Month': [moment().startOf('month'), moment().endOf('month')],
    }),
    [],
  )

  const handleFilter = useCallback(
    data => {
      const { dateRange, actionGroup } = data

      const types =
        isEmpty(data.types) &&
        !isNil(actionGroup) &&
        !isEmpty(
          intersection(
            [EVENT_METADATA_TYPES.INGREDIENT, EVENT_METADATA_TYPES.DISH_TYPE],
            eventMetadataTypes,
          ),
        ) &&
        some(data.subtypes, subtype =>
          includes(
            [
              EVENT_SUBTYPES.CREATE,
              EVENT_SUBTYPES.DELETE,
              EVENT_SUBTYPES.UPDATE,
            ],
            subtype,
          ),
        )
          ? [EVENT_TYPES.SAVE]
          : data.types
      // isEmpty(data.types) &&
      // !isNil(actionGroup) &&
      // some(data.subtypes, subtype => includes(keys(EVENT_TYPES), subtype))
      //   ? filter(
      //       map(data.subtypes, subtype => EVENT_TYPES[subtype]),
      //       type => !isNil(type),
      //     )
      //   : data.types

      const subtypes =
        isEmpty(data.subtypes) && !isNil(actionGroup)
          ? map(subtypeByActionGroupOptions[actionGroup], 'value')
          : data.subtypes

      const subtypesWithoutTypes = filter(
        subtypes,
        subtype => !includes(types, subtype),
      )

      const values = {
        ...omit(data, ['dateRange', 'actionGroup']),
        types,
        subtypes: subtypesWithoutTypes,
        startAt: get(dateRange, '[0]', null)?.format('YYYY-MM-DD HH:mm:ss'),
        endAt: get(dateRange, '[1]', null)?.format('YYYY-MM-DD HH:mm:ss'),
      }

      onFilter(values)
    },
    [onFilter, subtypeByActionGroupOptions, eventMetadataTypes],
  )

  const [selectedActionGroup, setSelectedActionGroup] = useState(null)

  return (
    <Form
      form={form}
      initialValues={initialValues}
      onFinish={handleFilter}
      onFieldsChange={(_, allFields) => {
        setToDirty()

        const newActionGroup = find(allFields, field =>
          includes(field.name, 'actionGroup'),
        )?.value

        setSelectedActionGroup(prevActionGroup => {
          if (prevActionGroup !== newActionGroup) {
            form.setFieldsValue({
              subtypes: [],
            })

            const values = reduce(
              allFields,
              (acc, field) => {
                if (!isEmpty(intersection(['dateRange'], field.name))) {
                  acc.startAt = get(field.value, '[0]', null)?.format(
                    'YYYY-MM-DD HH:mm:ss',
                  )
                  acc.endAt = get(field.value, '[1]', null)?.format(
                    'YYYY-MM-DD HH:mm:ss',
                  )
                  acc.dateRange = null
                } else if (!isEmpty(intersection(['subtypes'], field.name))) {
                  acc.subtypes = null
                } else if (
                  !isEmpty(intersection(['actionGroup'], field.name))
                ) {
                  acc.actionGroup = null
                } else {
                  acc[field.name[0]] = field.value
                }

                return acc
              },
              {},
            )

            onFilter(values)
          }
          return newActionGroup
        })
      }}
    >
      <Space>
        {shouldShowActionGroups ? (
          <>
            <FormSelect
              name="actionGroup"
              showSearch
              allowClear
              labelAbove
              label="Action"
              filterOption={filterSelectOptions}
              options={actionGroupOptions}
              placeholder="Filter by action groups..."
              dropdownMatchSelectWidth={DROPDOWN_MIN_WIDTH}
              style={{ minWidth: 200 }}
            />

            <FormSelect
              name="subtypes"
              showSearch
              allowClear
              labelAbove
              label="Subtypes"
              filterOption={filterSelectOptions}
              mode="multiple"
              maxTagCount={2}
              options={subtypeByActionGroupOptions[selectedActionGroup]}
              placeholder="Filter by feature/function..."
              dropdownMatchSelectWidth={DROPDOWN_MIN_WIDTH}
              style={{ minWidth: 200 }}
            />
          </>
        ) : (
          <>
            {filterEventTypes && (
              <FormSelect
                name="types"
                showSearch
                allowClear
                labelAbove
                label="Event Types"
                filterOption={filterSelectOptions}
                options={typeOptions}
                maxTagCount={2}
                mode="multiple"
                placeholder="Filter by event types..."
                dropdownMatchSelectWidth={DROPDOWN_MIN_WIDTH}
                style={{ minWidth: 200 }}
              />
            )}

            <FormSelect
              name="subtypes"
              showSearch
              allowClear
              labelAbove
              label="Event Subtypes"
              filterOption={filterSelectOptions}
              options={subtypeOptions}
              maxTagCount={2}
              mode="multiple"
              placeholder="Filter by event subtypes..."
              dropdownMatchSelectWidth={DROPDOWN_MIN_WIDTH}
              style={{ minWidth: 200 }}
            />
          </>
        )}
      </Space>

      <Space>
        <FormSelect
          name="createdByIds"
          showSearch
          allowClear
          labelAbove
          label="Users"
          filterOption={filterSelectOptions}
          options={userOptions}
          maxTagCount={2}
          mode="multiple"
          placeholder="Filter by users..."
          dropdownMatchSelectWidth={DROPDOWN_MIN_WIDTH}
          style={{ minWidth: 200 }}
        />
        <Form.Item
          label="Date Range"
          labelCol={getFormItemLabelAboveConfiguration(true)}
          name="dateRange"
        >
          <DatePicker.RangePicker
            label="Select Time Range"
            ranges={datePickerRangeOptions}
            format="YYYY/MM/DD HH:mm"
            showTime
          />
        </Form.Item>
      </Space>

      <FormInput
        style={{ width: '300px' }}
        isSearch
        name="searchTerm"
        labelAbove
        label="Search term in ingredient keywords or dictionary words"
        placeholder="Search term"
      />

      <FormInput
        style={{ width: '300px' }}
        isSearch
        name="exactSearchTerm"
        labelAbove
        label="Exact search term"
        placeholder="Exact search term"
      />

      <FormButtons
        permissions={[PERMISSIONS.EVENT.LIST]}
        isDisabled={!isDirty}
        onReset={resetForm}
        saveText="Search"
      />
    </Form>
  )
}

HistoryDrawerFilter.propTypes = {
  userOptions: PropTypes.arrayOf(optionPropType),
  createdByIds: PropTypes.arrayOf(idPropType),
  startAt: PropTypes.string,
  endAt: PropTypes.string,
  filterEventTypes: PropTypes.bool,
  eventMetadataTypes: PropTypes.arrayOf(PropTypes.string),
  types: PropTypes.arrayOf(PropTypes.string),
  subtypes: PropTypes.arrayOf(PropTypes.string),
  searchTerm: PropTypes.string,
  exactSearchTerm: PropTypes.string,
  onFilter: PropTypes.func.isRequired,
}

HistoryDrawerFilter.defaultProps = {
  filterEventTypes: false,
  eventMetadataTypes: [],
  types: [],
  subtypes: [],
  userOptions: [],
  createdByIds: [],
  searchTerm: undefined,
  exactSearchTerm: undefined,
  startAt: undefined,
  endAt: undefined,
}

export default HistoryDrawerFilter
